import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import type { CustomField, DashboardDetail, DashboardWidgetRow, WidgetSize, WidgetType } from "@astrum/shared";
import { WIDGET_SIZES, WIDGET_TYPES } from "@astrum/shared";
import { Button, Checkbox, Input, Modal, Select, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { GROUP_LABEL, METRIC_LABEL, SIZE_LABEL, WIDGET_CATALOGUE } from "../lib/dashboards.js";

type Config = Record<string, unknown>;
type Option = { id: string; label: string };

/** Add a widget (pick a type, then its settings) or change one's settings. */
export function WidgetDialog({
  open,
  dashboardId,
  widget,
  onClose,
  onSaved,
}: {
  open: boolean;
  dashboardId: string;
  widget: DashboardWidgetRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [type, setType] = useState<WidgetType | null>(null);
  const [title, setTitle] = useState("");
  const [size, setSize] = useState<WidgetSize | "">("");
  const [config, setConfig] = useState<Config>({});
  const [projects, setProjects] = useState<Option[]>([]);
  const [fieldProject, setFieldProject] = useState("");
  const [fields, setFields] = useState<Option[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setType(widget?.type ?? null);
    setTitle(widget?.title ?? "");
    setSize(widget?.size ?? "");
    setConfig((widget?.config as Config) ?? {});
    setFieldProject("");
  }, [open, widget]);

  const groupBy = config.groupBy as string | undefined;
  useEffect(() => {
    if (!open || type !== "tasks_by" || groupBy !== "custom_field") return;
    void api
      .get<{ projects: { id: string; code: string; name: string }[] }>("/projects")
      .then((r) => setProjects(r.projects.map((p) => ({ id: p.id, label: `${p.code} · ${p.name}` }))))
      .catch(() => setProjects([]));
  }, [open, type, groupBy]);

  useEffect(() => {
    if (!fieldProject) return setFields([]);
    void api
      .get<{ fields: CustomField[] }>(`/projects/${fieldProject}/custom-fields`)
      .then((r) => setFields(r.fields.filter((f) => f.type === "dropdown").map((f) => ({ id: f.id, label: f.name }))))
      .catch(() => setFields([]));
  }, [fieldProject]);

  const set = (key: string, value: unknown) => setConfig((c) => ({ ...c, [key]: value }));
  const str = (key: string, fallback: string) => (typeof config[key] === "string" ? (config[key] as string) : fallback);
  const num = (key: string, fallback: number) => (typeof config[key] === "number" ? (config[key] as number) : fallback);
  const listFilters = (config.filters as Record<string, unknown> | undefined) ?? {};
  const setListFilter = (key: string, value: unknown) => set("filters", { ...listFilters, [key]: value });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!type) return;
    setBusy(true);
    try {
      const body = { title: title.trim() || null, ...(size ? { size } : {}), config };
      if (widget) await api.patch(`/dashboards/${dashboardId}/widgets/${widget.id}`, body);
      else await api.post(`/dashboards/${dashboardId}/widgets`, { type, ...body });
      onSaved();
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not save the widget.", { tone: "danger" });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  if (!type) {
    return (
      <Modal open onClose={onClose} title="Add a widget">
        <div className="dash-types">
          {WIDGET_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed="false"
              onClick={() => {
                setType(t);
                setConfig({});
              }}
            >
              {WIDGET_CATALOGUE[t].label}
              <small>{WIDGET_CATALOGUE[t].hint}</small>
            </button>
          ))}
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title={widget ? `${WIDGET_CATALOGUE[type].label} settings` : `Add: ${WIDGET_CATALOGUE[type].label}`}>
      <form className="dash-form" onSubmit={(e) => void submit(e)}>
        <Input label="Title (optional)" value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} />
        <Select label="Size" value={size} onChange={(e) => setSize(e.target.value as WidgetSize | "")}>
          <option value="">Default</option>
          {WIDGET_SIZES.map((s) => (
            <option key={s} value={s}>
              {SIZE_LABEL[s]}
            </option>
          ))}
        </Select>

        {type === "number_tile" && (
          <Select label="Count" value={str("metric", "open")} onChange={(e) => set("metric", e.target.value)}>
            {Object.entries(METRIC_LABEL).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </Select>
        )}

        {type === "tasks_by" && (
          <>
            <Select label="Group by" value={str("groupBy", "status")} onChange={(e) => set("groupBy", e.target.value)}>
              {Object.entries(GROUP_LABEL).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
            {groupBy === "custom_field" && (
              <>
                <Select label="Project" value={fieldProject} onChange={(e) => setFieldProject(e.target.value)}>
                  <option value="">Choose a project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </Select>
                <Select label="Dropdown field" value={str("fieldId", "")} onChange={(e) => set("fieldId", e.target.value)}>
                  <option value="">Choose a field</option>
                  {fields.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </Select>
              </>
            )}
            <Select label="Chart" value={str("chart", "bar")} onChange={(e) => set("chart", e.target.value)}>
              <option value="bar">Bar</option>
              <option value="donut">Donut</option>
            </Select>
          </>
        )}

        {type === "workload" && (
          <Select label="Weeks ahead" value={String(num("weeks", 4))} onChange={(e) => set("weeks", Number(e.target.value))}>
            {[1, 2, 4, 6, 8].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        )}

        {type === "time_logged" && (
          <Select label="Group by" value={str("groupBy", "person")} onChange={(e) => set("groupBy", e.target.value)}>
            <option value="person">Person</option>
            <option value="project">Project</option>
          </Select>
        )}

        {type === "goals_progress" && (
          <Input label="Period (optional)" hint="Such as 2026 Q4. Blank shows the current one." value={str("periodLabel", "")} onChange={(e) => set("periodLabel", e.target.value || undefined)} />
        )}

        {type === "task_list" && (
          <>
            <Input label="Statuses" hint="Comma separated: todo, in_progress, review, done." value={String(listFilters.status ?? "")} onChange={(e) => setListFilter("status", e.target.value || undefined)} />
            <Input label="Priorities" hint="Comma separated: low, normal, medium, high, urgent." value={String(listFilters.priority ?? "")} onChange={(e) => setListFilter("priority", e.target.value || undefined)} />
            <Input label="Title contains" value={String(listFilters.q ?? "")} onChange={(e) => setListFilter("q", e.target.value || undefined)} />
            <Checkbox label="Only my tasks" checked={listFilters.mine === true} onChange={(e) => setListFilter("mine", e.target.checked || undefined)} />
            <Checkbox label="Only overdue" checked={listFilters.overdue === true} onChange={(e) => setListFilter("overdue", e.target.checked || undefined)} />
          </>
        )}

        {["workload", "budget_burn", "goals_progress", "project_health", "task_list"].includes(type) && (
          <Select label="Rows per page" value={String(num("limit", 10))} onChange={(e) => set("limit", Number(e.target.value))}>
            {[5, 10, 15, 25].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        )}

        <div className="dash-form__actions">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            {widget ? "Save" : "Add widget"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Who else may open the dashboard: the office, teams, or named people. */
export function ShareDialog({
  open,
  dashboard,
  onClose,
  onSaved,
}: {
  open: boolean;
  dashboard: DashboardDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [office, setOffice] = useState(false);
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [teams, setTeams] = useState<{ id: string; name: string }[]>([]);
  const [people, setPeople] = useState<{ id: string; displayName: string }[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOffice(dashboard.sharing?.office ?? false);
    setTeamIds(dashboard.sharing?.teamIds ?? []);
    setUserIds(dashboard.sharing?.userIds ?? []);
    void api.get<{ teams: { id: string; name: string }[] }>("/teams?limit=200").then((r) => setTeams(r.teams)).catch(() => setTeams([]));
    void api.get<{ people: { id: string; displayName: string }[] }>("/people").then((r) => setPeople(r.people)).catch(() => setPeople([]));
  }, [open, dashboard]);

  const toggle = (list: string[], set: (v: string[]) => void, id: string) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/dashboards/${dashboard.id}`, { sharing: { office, teamIds, userIds } });
      onSaved();
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not save sharing.", { tone: "danger" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Share dashboard">
      <div className="dash-form">
        <p className="dash-widget__note">Others can view this dashboard but not change it. Each person sees figures for the projects they can see.</p>
        <Checkbox label="Everyone in this office" checked={office} onChange={(e) => setOffice(e.target.checked)} />
        {teams.length > 0 && (
          <fieldset className="dash-check">
            <legend>Teams</legend>
            {teams.map((t) => (
              <Checkbox key={t.id} label={t.name} checked={teamIds.includes(t.id)} onChange={() => toggle(teamIds, setTeamIds, t.id)} />
            ))}
          </fieldset>
        )}
        <fieldset className="dash-check">
          <legend>People</legend>
          {people
            .filter((p) => p.id !== dashboard.ownerId)
            .map((p) => (
              <Checkbox key={p.id} label={p.displayName} checked={userIds.includes(p.id)} onChange={() => toggle(userIds, setUserIds, p.id)} />
            ))}
        </fieldset>
        <div className="dash-form__actions">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => void save()}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}
